export const EWSS_GRADES = [
  "A+",
  "A",
  "A-",
  "B+",
  "B",
  "B-",
  "C+",
  "C",
  "C-",
  "D+",
  "D",
  "D-",
];

export const EWSS_THRESHOLD_OPERATORS = [
  { value: "eq", label: "strictement égal à" },
  { value: "lt", label: "strictement inférieur à" },
  { value: "lte", label: "inférieur ou égal à" },
  { value: "gt", label: "strictement supérieur à" },
  { value: "gte", label: "supérieur ou égal à" },
];

export const ILLUSTRATION_FILTER_OPTIONS = [
  { value: "", label: "Avec ou sans" },
  { value: "only", label: "Uniquement" },
  { value: "none", label: "Non" },
];

export const WORD_ORIGIN_FILTER_OPTIONS = [
  { value: "", label: "— Tous —" },
  { value: "catalog", label: "Catalogue" },
  { value: "captures", label: "Captures" },
];

export const GROUPEMENT_MODE_OPTIONS = ILLUSTRATION_FILTER_OPTIONS;

export const GROUPEMENT_FILTER_NONE = "none";

export function sanitizeGroupementFilter(raw) {
  return String(raw ?? "").trim();
}

export function toGroupementFilterOptions(groupements = []) {
  return [
    { value: "", label: "— Tous —" },
    { value: GROUPEMENT_FILTER_NONE, label: "Aucun groupement" },
    ...toGroupementThemeOptions(groupements).slice(1),
  ];
}

export function toGroupementThemeOptions(groupements = []) {
  return [
    { value: "", label: "Toutes les thématiques" },
    ...groupements
      .filter((item) => item?.groupementId)
      .map((item) => ({
        value: item.groupementId,
        label: String(item.title || "").trim() || "Sans titre",
      })),
  ];
}

const VALID_ILLUSTRATION_FILTERS = new Set(["only", "none"]);
const VALID_WORD_ORIGIN_FILTERS = new Set(["catalog", "captures"]);

export function sanitizeIllustrationFilter(raw) {
  const value = String(raw ?? "").trim();
  return VALID_ILLUSTRATION_FILTERS.has(value) ? value : "";
}

export function sanitizeWordOriginFilter(raw) {
  const value = String(raw ?? "").trim();
  return VALID_WORD_ORIGIN_FILTERS.has(value) ? value : "";
}

export function sanitizeGroupementModeFilters(raw = {}) {
  let groupement = sanitizeIllustrationFilter(raw.groupement);
  let groupementId = sanitizeGroupementFilter(raw.groupementId);
  if (!groupement && groupementId === GROUPEMENT_FILTER_NONE) {
    groupement = "none";
    groupementId = "";
  } else if (!groupement && groupementId) {
    groupement = "only";
  } else if (groupement !== "only") {
    groupementId = "";
  } else if (groupementId === GROUPEMENT_FILTER_NONE) {
    groupementId = "";
  }
  return { groupement, groupementId };
}

export function wordMatchesGroupementMode(word, groupement, groupementId) {
  if (groupement === "none") return !word?.groupementId;
  if (groupement !== "only") return true;
  return groupementId
    ? word?.groupementId === groupementId
    : Boolean(word?.groupementId);
}

export const NO_EWSS_GAME_COUNT_OPTIONS = [
  { value: "", label: "— Tous —" },
  { value: "never", label: "Jamais joués" },
  { value: "once", label: "Joués 1 fois" },
  { value: "twice", label: "Joués 2 fois" },
  { value: "onceOrTwice", label: "Joués 1 ou 2 fois" },
];

export const PAQUET_FILTER_OPTIONS = [
  { value: "1", label: "Paquet 1" },
  { value: "2", label: "Paquet 2" },
  { value: "3", label: "Paquet 3" },
  { value: "4", label: "Paquet 4" },
  { value: "5", label: "Paquet 5" },
];

/** Valeurs acceptées (dont alias legacy « hors » → paquet 5). */
const VALID_PAQUET_FILTER_VALUES = new Set(["1", "2", "3", "4", "5", "hors"]);

function normalizePaquetFilterValue(raw) {
  const value = String(raw ?? "").trim();
  if (!VALID_PAQUET_FILTER_VALUES.has(value)) return "";
  return value === "hors" ? "5" : value;
}

export function sanitizeRegisterPaquets(raw) {
  const values = !Array.isArray(raw)
    ? typeof raw === "string" && raw.trim()
      ? [raw.trim()]
      : []
    : raw;
  return [...new Set(values.map((value) => normalizePaquetFilterValue(value)).filter(Boolean))];
}

export const POTION_AID_FILTER_OPTIONS = [
  { value: "memory", label: "Mémoire" },
  { value: "time", label: "Temps" },
  { value: "either", label: "Mémoire ou temps" },
  { value: "both", label: "Mémoire et temps" },
];

export const REGISTER_CONSECUTIVE_FAIL_OPTIONS = [2, 3, 4, 5];

export const CAPTURE_DATE_MASK = "JJ/MM/AAAA";
export const TIME_MASK = "HH:MM";

export function maskTimeInput(raw) {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 4);
  const hours = digits.slice(0, 2);
  const minutes = digits.slice(2, 4);
  let result = hours;
  if (digits.length > 2) result += `:${minutes}`;
  return result;
}

export function sanitizeTimeInput(raw) {
  const match = String(raw ?? "")
    .trim()
    .match(/^(\d{2}):(\d{2})$/);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return "";
  return `${match[1]}:${match[2]}`;
}

export function maskCaptureDateInput(raw, previous = "") {
  const next = String(raw ?? "");
  const prev = String(previous ?? "");
  const nextHasSlash = next.includes("/");
  const prevHasSlash = prev.includes("/");

  if (nextHasSlash || prevHasSlash) {
    const parts = next.split("/");
    const day = (parts[0] || "").replace(/\D/g, "").slice(0, 2);
    const rest = (parts[1] || "").replace(/\D/g, "");
    let month = "";
    let year = "";

    if (parts.length >= 3) {
      month = rest.slice(0, 2);
      year = parts.slice(2).join("").replace(/\D/g, "").slice(0, 4);
    } else if (parts.length === 2) {
      month = rest.slice(0, 2);
      year = rest.slice(2, 6);
    }

    if (!day && !month && !year) return "";
    if (!month && !year) return parts.length >= 2 ? `${day}/` : day;
    if (!year) return `${day}/${month}`;
    return `${day}/${month}/${year}`;
  }

  const digits = next.replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  let result = day;
  if (digits.length > 2) result += `/${month}`;
  if (digits.length > 4) result += `/${year}`;
  return result;
}

export function sanitizeCaptureDate(raw) {
  const match = String(raw ?? "")
    .trim()
    .match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const test = new Date(year, month - 1, day);
  if (
    test.getFullYear() !== year ||
    test.getMonth() !== month - 1 ||
    test.getDate() !== day
  ) {
    return "";
  }
  return `${match[1]}/${match[2]}/${match[3]}`;
}

function captureDateToComparable(value) {
  const sanitized = sanitizeCaptureDate(value);
  if (!sanitized) return null;
  const [day, month, year] = sanitized.split("/").map(Number);
  return year * 10000 + month * 100 + day;
}

export function normalizeCaptureDateRange(startRaw, endRaw) {
  const start = sanitizeCaptureDate(startRaw);
  const end = sanitizeCaptureDate(endRaw);
  if (start && end) {
    const startNum = captureDateToComparable(start);
    const endNum = captureDateToComparable(end);
    if (startNum != null && endNum != null && startNum > endNum) {
      return { start: end, end: start };
    }
  }
  return { start, end };
}

function playDateTimeToComparable(date, time, endOfDay) {
  const dayNum = captureDateToComparable(date);
  if (dayNum == null) return null;
  let hm;
  if (time) {
    const [h, m] = time.split(":").map(Number);
    hm = h * 100 + m;
  } else {
    hm = endOfDay ? 2359 : 0;
  }
  return dayNum * 10000 + hm;
}

// Normalise l'intervalle date+heure de jeu ; l'heure n'a de sens qu'avec une date,
// et on inverse les deux bornes ensemble si elles sont à l'envers.
export function normalizePlayDateTimeRange(startDate, startTime, endDate, endTime) {
  const sDate = sanitizeCaptureDate(startDate);
  const eDate = sanitizeCaptureDate(endDate);
  let sTime = sanitizeTimeInput(startTime);
  let eTime = sanitizeTimeInput(endTime);
  if (!sDate) sTime = "";
  if (!eDate) eTime = "";

  if (sDate && eDate) {
    const sNum = playDateTimeToComparable(sDate, sTime, false);
    const eNum = playDateTimeToComparable(eDate, eTime, true);
    if (sNum != null && eNum != null && sNum > eNum) {
      return { startDate: eDate, startTime: eTime, endDate: sDate, endTime: sTime };
    }
  }
  return { startDate: sDate, startTime: sTime, endDate: eDate, endTime: eTime };
}

export const PLAYED_COUNT_FILTER_MAX = 20;

export const PLAYED_COUNT_FILTER_OPTIONS = Array.from(
  { length: PLAYED_COUNT_FILTER_MAX + 1 },
  (_, index) => index
);

export function sanitizePlayedCountBound(raw) {
  if (raw === "" || raw == null) return "";
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > PLAYED_COUNT_FILTER_MAX) return "";
  return String(Math.floor(value));
}

export function normalizePlayedCountRange(minRaw, maxRaw) {
  const min = sanitizePlayedCountBound(minRaw);
  const max = sanitizePlayedCountBound(maxRaw);
  if (min === "" || max === "") return { min, max };
  if (Number(min) > Number(max)) return { min: max, max: min };
  return { min, max };
}

export const DEFAULT_REGISTER_FILTERS = {
  ewssGrade: "",
  ewssBelowGrade: "",
  ewssAboveGrade: "",
  ewssStrictBelowGrade: "",
  ewssStrictAboveGrade: "",
  noEwssGameCount: "",
  lastAppearanceFailed: "",
  consecutiveFails: "",
  consecutiveFailsCount: 2,
  playedCountMin: "",
  playedCountMax: "",
  potionsUsed: "",
  potionsUsedFilter: "memory",
  includeNoyau: "",
  includeFavoris: "",
  includePrioritaires: "",
  paquets: [],
  livre: "",
  auteur: "",
  captureDateStart: "",
  captureDateEnd: "",
  playDateStart: "",
  playDateEnd: "",
  playTimeStart: "",
  playTimeEnd: "",
  illustration: "",
  wordOrigin: "",
  groupement: "",
  groupementId: "",
};

function sanitizeYesFlag(raw) {
  return String(raw ?? "").trim() === "yes" ? "yes" : "";
}

export function sanitizeRegisterFilters(raw = DEFAULT_REGISTER_FILTERS) {
  const filters = { ...DEFAULT_REGISTER_FILTERS, ...raw };
  filters.paquets = sanitizeRegisterPaquets(filters.paquets);
  filters.includeNoyau = sanitizeYesFlag(filters.includeNoyau);
  filters.includeFavoris = sanitizeYesFlag(filters.includeFavoris);
  filters.includePrioritaires = sanitizeYesFlag(filters.includePrioritaires);
  const playedRange = normalizePlayedCountRange(filters.playedCountMin, filters.playedCountMax);
  filters.playedCountMin = playedRange.min;
  filters.playedCountMax = playedRange.max;

  const captureRange = normalizeCaptureDateRange(
    filters.captureDateStart,
    filters.captureDateEnd
  );
  filters.captureDateStart = captureRange.start;
  filters.captureDateEnd = captureRange.end;

  const playRange = normalizePlayDateTimeRange(
    filters.playDateStart,
    filters.playTimeStart,
    filters.playDateEnd,
    filters.playTimeEnd
  );
  filters.playDateStart = playRange.startDate;
  filters.playTimeStart = playRange.startTime;
  filters.playDateEnd = playRange.endDate;
  filters.playTimeEnd = playRange.endTime;

  filters.illustration = sanitizeIllustrationFilter(filters.illustration);
  filters.wordOrigin = sanitizeWordOriginFilter(filters.wordOrigin);
  const grouping = sanitizeGroupementModeFilters(filters);
  filters.groupement = grouping.groupement;
  filters.groupementId = grouping.groupementId;

  // Anciens sous-filtres noyau favoris/prioritaires — obsolètes
  delete filters.noyauFavoris;
  delete filters.noyauPrioritaires;
  delete filters.noyauOnly;

  return filters;
}

export function isRegisterFilterActive(filters = DEFAULT_REGISTER_FILTERS) {
  const normalized = sanitizeRegisterFilters(filters);

  if (isPlayedCountRangeFilterActive(normalized.playedCountMin, normalized.playedCountMax)) {
    return true;
  }

  return Object.entries(normalized).some(([key, value]) => {
    if (key === "potionsUsedFilter" && normalized.potionsUsed !== "yes") return false;
    if (key === "groupementId" && normalized.groupement !== "only") return false;
    if (key === "consecutiveFailsCount" && normalized.consecutiveFails !== "yes") return false;
    if (key === "playedCountMin" || key === "playedCountMax") return false;
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  });
}

function isPlayedCountRangeFilterActive(min, max) {
  return Boolean(sanitizePlayedCountBound(min) !== "" || sanitizePlayedCountBound(max) !== "");
}

export function buildRegisterFilterQuery(rawFilters = DEFAULT_REGISTER_FILTERS) {
  const filters = sanitizeRegisterFilters(rawFilters);
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (
      key === "includeNoyau" ||
      key === "includeFavoris" ||
      key === "includePrioritaires"
    ) {
      return;
    }
    if (key === "potionsUsedFilter" && filters.potionsUsed !== "yes") return;
    if (key === "groupementId" && filters.groupement !== "only") return;
    if (key === "consecutiveFailsCount" && filters.consecutiveFails !== "yes") return;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(","));
      return;
    }
    if (value) params.set(key, value);
  });

  if (filters.includeNoyau === "yes") params.set("includeNoyau", "yes");
  if (filters.includeFavoris === "yes") params.set("includeFavoris", "yes");
  if (filters.includePrioritaires === "yes") params.set("includePrioritaires", "yes");

  return params.toString();
}
